import { forwardRef, useState } from "react";

import { Autocomplete, Box, CircularProgress, Typography } from "@mui/material";
import PropTypes from "prop-types";

import { apiClient } from "@/features/shared";
import { CustomInputField } from "@/features/ui";
import { chartOfAccountTypeLabels } from "../chart-of-account-type";

export const ChartOfAccountSelect = forwardRef(
  ({ leafOnly = true, types, TextFieldProps, onChange, ...props }, ref) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleOpen = async () => {
      setOpen(true);

      if (options.length > 0) return;

      setLoading(true);
      try {
        const {
          data: { data },
        } = await apiClient.get("chart-of-accounts", {
          params: {
            page: 1,
            perPage: 1000,
            ...(leafOnly && { leafOnly: true }),
            ...(types?.length > 0 && { types }),
          },
        });
        setOptions(data || []);
      } finally {
        setLoading(false);
      }
    };

    const handleClose = () => {
      setOpen(false);
    };

    return (
      <Autocomplete
        ref={ref}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={(_, newValue) => onChange(newValue)}
        options={options}
        loading={loading}
        getOptionLabel={(option) => `${option.code} - ${option.name}`}
        renderOption={(renderProps, option) => {
          const { key, ...otherProps } = renderProps;
          return (
            <Box component="li" key={key} {...otherProps}>
              <Box>
                <Typography variant="body2">
                  {option.code} - {option.name}
                </Typography>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {chartOfAccountTypeLabels[option.type]}
                </Typography>
              </Box>
            </Box>
          );
        }}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderInput={(params) => (
          <CustomInputField
            {...TextFieldProps}
            {...params}
            InputProps={{
              ...params.slotProps.input,
              ...TextFieldProps?.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...props}
      />
    );
  },
);

ChartOfAccountSelect.displayName = "ChartOfAccountSelect";

ChartOfAccountSelect.propTypes = {
  leafOnly: PropTypes.bool,
  types: PropTypes.arrayOf(PropTypes.string),
  TextFieldProps: PropTypes.object,
  onChange: PropTypes.func,
};
